// ClearFund Trading Terminal — shared constants
// Auto-extracted from monolith v2.5

/* eslint-disable */
// ============================================================
// CLEARFUND TRADING TERMINAL  v2.5
// Usage in App.js — replace TradingPage with:
//   <TradingTerminal
//     account={realAccount || mockAccount}
//     onNavigate={setTraderPage}
//     onAccountRefresh={...}
//   />
//
// FEATURES:
//   • Canvas candlestick chart — zoom, pan, price-axis pan
//   • Drawing tools: horizontal lines, trendlines (H/T keys)
//   • Order panel: Market / Limit / Stop with price input
//   • Quantity presets & keyboard trading (B/S/F keys)
//   • Live Bid/Ask display with spread in ticks
//   • Risk Calculator: 1/5/10 tick risk + % of balance
//   • Price Alerts: set from context menu or panel, drawn on chart
//   • Watchlist: click any symbol to switch chart
//   • Performance Calendar: daily P&L heatmap + yearly view
//   • Trade Stats: win rate, profit factor, R:R, best/worst
//   • Positions: P&L% column; Trades history: % return column
//   • EMA/SMA indicators: toggle panel, add custom, right-click remove
//   • Candle countdown timer (next bar close)
//   • Scroll to pan · Ctrl+Scroll to zoom
//   • Equity curve sparkline in Stats tab
//   • Auto-refresh positions every 5s
//   • Crosshair time label on X axis
//   • Daily High / Low / Previous Close reference lines on chart
//   • Right-click any drawing to delete it
//   • Full-screen chart mode (Shift+F or ⛶ button)
//   • Collapsible right panel (‹ toggle arrow)
//   • Tick P&L column in positions table
//   • Sharpe ratio, win/loss streaks, avg hold time in Stats
//   • DOM price ladder panel with depth visualization
//   • Daily P&L goal bar + max loss warning bar
//   • Fibonacci retracement tool (F key) + Rectangle tool (R key)
//   • Break-even stop button (one-click move stop to entry)
//   • Trailing stop with configurable tick distance
//   • All 16 timeframes via "more ▾" dropdown
//   • RSI sub-panel below chart (toggleable via indicators)
//   • Bollinger Bands + VWAP indicator overlays
//   • Bracket orders: SL/TP in ticks with R:R display
//   • Audio alerts: fills, closes, price alerts, errors (mutable)
//   • Session / day-boundary lines on chart
//   • Trade Journal: add notes to every closed trade
//   • Keyboard shortcuts overlay (? key)
//   • LIVE/SIM animated connection indicator
//   • Resizable panels (drag handles)
//   • Context menu: drawings, zoom reset, set alert
//   • Chart settings modal with 9 sections
// ============================================================
import { useState, useEffect, useRef, useCallback } from 'react';
import { trades as tradesApi, accounts } from './lib/api';
import { useMarketData } from './lib/useMarketData';

// ─── INSTRUMENTS ─────────────────────────────────────────────
const INSTRUMENTS = {
  NQ:  { name:"E-mini Nasdaq 100",   exchange:"CME",   tickSize:0.25,      tickVal:5,      category:"EQUITY INDEX" },
  ES:  { name:"E-mini S&P 500",      exchange:"CME",   tickSize:0.25,      tickVal:12.5,   category:"EQUITY INDEX" },
  YM:  { name:"E-mini Dow Jones",    exchange:"CBOT",  tickSize:1,         tickVal:5,      category:"EQUITY INDEX" },
  RTY: { name:"E-mini Russell 2000", exchange:"CME",   tickSize:0.1,       tickVal:5,      category:"EQUITY INDEX" },
  MNQ: { name:"Micro Nasdaq 100",    exchange:"CME",   tickSize:0.25,      tickVal:0.5,    category:"MICRO" },
  MES: { name:"Micro S&P 500",       exchange:"CME",   tickSize:0.25,      tickVal:1.25,   category:"MICRO" },
  MYM: { name:"Micro Dow Jones",     exchange:"CBOT",  tickSize:1,         tickVal:0.5,    category:"MICRO" },
  M2K: { name:"Micro Russell 2000",  exchange:"CME",   tickSize:0.1,       tickVal:0.5,    category:"MICRO" },
  CL:  { name:"Crude Oil WTI",       exchange:"NYMEX", tickSize:0.01,      tickVal:10,     category:"ENERGY" },
  NG:  { name:"Natural Gas",         exchange:"NYMEX", tickSize:0.001,     tickVal:10,     category:"ENERGY" },
  RB:  { name:"RBOB Gasoline",       exchange:"NYMEX", tickSize:0.0001,    tickVal:4.2,    category:"ENERGY" },
  GC:  { name:"Gold",                exchange:"COMEX", tickSize:0.1,       tickVal:10,     category:"METALS" },
  SI:  { name:"Silver",              exchange:"COMEX", tickSize:0.005,     tickVal:25,     category:"METALS" },
  HG:  { name:"Copper",              exchange:"COMEX", tickSize:0.0005,    tickVal:12.5,   category:"METALS" },
  ZC:  { name:"Corn",                exchange:"CBOT",  tickSize:0.25,      tickVal:12.5,   category:"AGRICULTURE" },
  ZS:  { name:"Soybeans",            exchange:"CBOT",  tickSize:0.25,      tickVal:12.5,   category:"AGRICULTURE" },
  ZW:  { name:"Wheat",               exchange:"CBOT",  tickSize:0.25,      tickVal:12.5,   category:"AGRICULTURE" },
  ZB:  { name:"30-Year T-Bond",      exchange:"CBOT",  tickSize:0.03125,   tickVal:31.25,  category:"RATES" },
  ZN:  { name:"10-Year T-Note",      exchange:"CBOT",  tickSize:0.015625,  tickVal:15.625, category:"RATES" },
  ZF:  { name:"5-Year T-Note",       exchange:"CBOT",  tickSize:0.0078125, tickVal:7.8125, category:"RATES" },
  '6E':{ name:"Euro FX",             exchange:"CME",   tickSize:0.00005,   tickVal:6.25,   category:"FX" },
  '6J':{ name:"Japanese Yen",        exchange:"CME",   tickSize:0.0000005, tickVal:6.25,   category:"FX" },
  '6B':{ name:"British Pound",       exchange:"CME",   tickSize:0.0001,    tickVal:6.25,   category:"FX" },
};
const CATEGORIES = ["EQUITY INDEX","MICRO","ENERGY","METALS","AGRICULTURE","RATES","FX"];
const BASE_PRICES = {
  NQ:21850,ES:5900,YM:44200,RTY:2280,MNQ:21850,MES:5900,MYM:44200,M2K:2280,
  CL:78.5,NG:2.85,RB:2.41,GC:2650,SI:30.5,HG:4.45,
  ZC:458,ZS:1050,ZW:575,ZB:119.5,ZN:111.5,ZF:107.5,
  '6E':1.0850,'6J':0.00660,'6B':1.2650,
};
const TIMEFRAMES = ["1m","2m","3m","5m","10m","15m","30m","1h","2h","4h","6h","8h","12h","1D","3D","1W"];
const TF_INTERVALS = {"1m":700,"2m":1100,"3m":1500,"5m":2000,"10m":2500,"15m":3000,"30m":3500,"1h":5000,"2h":6000,"4h":7000,"6h":8000,"8h":9000,"12h":10000,"1D":12000,"3D":14000,"1W":16000};
const TF_VOL = {"1m":0.03,"2m":0.05,"3m":0.07,"5m":0.12,"10m":0.18,"15m":0.25,"30m":0.4,"1h":0.6,"2h":0.9,"4h":1.3,"6h":1.6,"8h":1.9,"12h":2.3,"1D":3.5,"3D":5.0,"1W":8.0};

const DEFAULT_SETTINGS = {
  bgColor:"#0A0A0A", bgStyle:"solid",
  gridLines:"solid", scaleLinesColor:"#1f1f1f",
  crosshairStyle:"cross", crosshairColor:"#555555",
  watermark:"hidden", watermarkColor:"#1a1a1a",
  scaleTextColor:"#555555", scaleTextSize:9,
  marginTop:10, marginBottom:8, marginRight:10,
  candleStyle:"candle",
  upColor:"#00C896", downColor:"#FF3B3B",
  upBorderColor:"#00C896", downBorderColor:"#FF3B3B",
  upWickColor:"#00C896", downWickColor:"#FF3B3B",
  borderWidth:1, wickWidth:1,
  showVolume:true,
  volumeUpColor:"rgba(0,200,150,0.25)", volumeDownColor:"rgba(255,59,59,0.25)",
  showOHLC:true, showVolInStatus:true, showChangeInStatus:true,
  showPriceScale:true, showTimeScale:true, priceScalePosition:"right", autoScale:true,
  showPositionLines:true, showPnlOnChart:true, positionLineColor:"#F5A623",
  alertLineColor:"#A78BFA", alertLineStyle:"dashed",
};

const fmt = {
  usd:(n,dec=0)=>new Intl.NumberFormat("en-US",{style:"currency",currency:"USD",minimumFractionDigits:dec,maximumFractionDigits:dec}).format(n),
  price:(n,sym)=>{const inst=INSTRUMENTS[sym];if(!inst)return n?.toFixed(2)??"—";const d=inst.tickSize<0.01?5:inst.tickSize<0.1?3:2;return n?.toFixed(d)??"—";},
  time:()=>new Date().toLocaleTimeString("en-US",{hour12:false}),
};

// ─── COLOR PALETTE — full 14×10 grid ─────────────────────────
const PALETTE = [
  ["#ffffff","#e0e3eb","#c8ccd7","#a3a8b8","#787b86","#6a6d78","#4c525e","#363c4e","#2a2e39","#1e2230","#131722","#0c0e17","#080a10","#000000"],
  ["#fff0f0","#ffd6d6","#ffb3b3","#ff8a80","#ff5252","#f23645","#e53935","#c62828","#b71c1c","#7f0000","#ff6d00","#e65100","#bf360c","#8d2600"],
  ["#fff8e1","#ffecb3","#ffe082","#ffd54f","#ffca28","#ffb300","#ffa000","#ff8f00","#ff6f00","#e65100","#ffcd3c","#f9a825","#f57f17","#bf360c"],
  ["#f1f8e9","#dcedc8","#c5e1a5","#aed581","#9ccc65","#8bc34a","#7cb342","#558b2f","#33691e","#1b5e20","#089981","#00897b","#00695c","#004d40"],
  ["#e0f7fa","#b2ebf2","#80deea","#4dd0e1","#26c6da","#00bcd4","#0097a7","#00838f","#006064","#004d40","#00b8d4","#0091ea","#0277bd","#01579b"],
  ["#e8eaf6","#c5cae9","#9fa8da","#7986cb","#5c6bc0","#3f51b5","#3949ab","#303f9f","#283593","#1a237e","#1e53e5","#1565c0","#0d47a1","#0a2d6e"],
  ["#f3e5f5","#e1bee7","#ce93d8","#ba68c8","#ab47bc","#9c27b0","#8e24aa","#7b1fa2","#6a1b9a","#4a148c","#673ab7","#5e35b1","#4527a0","#311b92"],
  ["#fce4ec","#f8bbd0","#f48fb1","#f06292","#ec407a","#e91e63","#d81b60","#c2185b","#ad1457","#880e4f","#e040fb","#d500f9","#aa00ff","#7c4dff"],
  ["#f7525f","#ff9800","#4caf50","#00bcd4","#2196f3","#9c27b0","#ff5722","#795548","#607d8b","#455a64","#26a69a","#42a5f5","#ab47bc","#ef5350"],
  ["#00C896","#00FF9D","#089981","#05a36d","#26a69a","#FF3B3B","#FF6B6B","#f23645","#e53935","#c62828","#F5A623","#ff9800","#4DB8FF","#A78BFA"],
];


// Real millisecond duration per bar (for candle timestamps and event matching)
const TF_MS = {"1m":60000,"2m":120000,"3m":180000,"5m":300000,"10m":600000,
  "15m":900000,"30m":1800000,"1h":3600000,"2h":7200000,"4h":14400000,
  "6h":21600000,"8h":28800000,"12h":43200000,"1D":86400000,"3D":259200000,"1W":604800000};

export { INSTRUMENTS, CATEGORIES, TIMEFRAMES, PALETTE, BASE_PRICES, fmt, DEFAULT_SETTINGS, TF_VOL, TF_INTERVALS, TF_MS };
